import type { ComponentType, SVGProps } from 'react';
import React from 'react';

// convert all SVG files to static import (SVGR converts SVG to React component)
// to apply currentColor, we need to statically import the SVG files.
import Measure from '@/assets/svg/measure.svg';

// Lines
import TrendLine from '@/assets/svg/trend_line.svg';
import Ray from '@/assets/svg/ray.svg';
import InfoLine from '@/assets/svg/info_line.svg';
import HorzLine from '@/assets/svg/horz_line.svg';
import VertLine from '@/assets/svg/vert_line.svg';
import ParallelChannel from '@/assets/svg/parallel_channel.svg';

// Fibonacci
import FibRetracement from '@/assets/svg/fib_retracement.svg';
import FibChannel from '@/assets/svg/fib_channel.svg';

// Gann
import GannFan from '@/assets/svg/gann_fan.svg';
import GannBox from '@/assets/svg/gann_box.svg';

// Patterns
import ElliottImpulse12345 from '@/assets/svg/elliott_impulse_12345.svg';
import ElliottCorrectionABC from '@/assets/svg/elliott_correction_abc.svg';
import XABCDPattern from '@/assets/svg/xabcd_pattern.svg';

// Shapes
import Triangle from '@/assets/svg/triangle.svg';
import Rectangle from '@/assets/svg/rectangle.svg';
import Path from '@/assets/svg/path.svg';

import Text from '@/assets/svg/text.svg';
import OrderLinePattern from '@/assets/svg/order_line_pattern.svg';
import OrderLineLong from '@/assets/svg/order_line_long.svg';
import OrderLineShort from '@/assets/svg/order_line_short.svg';
import FixedRangeVolumeProfile from '@/assets/svg/volume_profile.svg';
import AnchoredVolumeProfile from '@/assets/svg/anchored_volume_profile.svg';
// ShapeListView
import ShapeListView from '@/assets/svg/shape_list_view.svg';

// Line Styles
import Dash from '@/assets/svg/dash.svg';
import Solid from '@/assets/svg/solid.svg';
import ShortDot from '@/assets/svg/short_dot.svg';

// icon map (file name -> React component)
const svgMap: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  Measure,

  // Lines
  TrendLine,
  Ray,
  InfoLine,
  HorzLine,
  VertLine,
  ParallelChannel,

  // Fibonacci
  FibRetracement,
  FibChannel,

  // Gann
  GannFan,
  GannBox,

  // Patterns
  ElliottImpulse12345,
  ElliottCorrectionABC,
  XABCDPattern,

  // Shapes
  Rectangle,
  Triangle,
  Path,

  Text,

  OrderLineLong,
  OrderLineShort,
  OrderLinePattern,
  FixedRangeVolumeProfile,
  AnchoredVolumeProfile,

  // ShapeListView
  ShapeListView,

  // Line Styles
  Dash,
  Solid,
  ShortDot,
};

interface DynamicSvgProps extends SVGProps<SVGSVGElement> {
  /** SVG file name (without extension) */
  iconName: string;
  title?: string;
}

/**
 * Dynamically render SVG icons
 *
 * If the fill/stroke property of the SVG is set to "currentColor",
 * the parent's color CSS property will be applied.
 *
 * **Important**: When adding a new SVG file, add the import to the top of this file and
 * register it in the iconMap object.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DynamicSvg iconName="TrendLine" />
 *
 * // Apply color (SVG must have currentColor)
 * <DynamicSvg
 *   iconName="TrendLine"
 *   style={{ color: 'red' }}
 * />
 *
 * // Scale the icon
 * <DynamicSvg
 *   iconName="TrendLine"
 *   width={24}
 *   height={24}
 * />
 * ```
 */
export function DynamicSvg({ iconName, title, ...svgProps }: DynamicSvgProps) {
  const SvgComponent = svgMap[iconName];

  if (!SvgComponent) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`SVG icon "${iconName}" not found. Available icons:`, Object.keys(svgMap));
    }
    return null;
  }

  return (
    <SvgComponent
      // @ts-ignore : ignore typescript complaining about the title prop.
      title={title}
      {...svgProps}
      style={{ overflow: 'visible' }}
    />
  );
}
